import { Box, Collapse, DialogActions, DialogContent, FormHelperText, Grid, Typography } from "@material-ui/core";
import { CustomPriceField, NumberField, Section, SelectField, TextField } from "@microrealestate/commonui/components";
import { Form, Formik } from "formik";
import { observer } from "mobx-react-lite";
import useTranslation from "next-translate/useTranslation";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import * as Yup from "yup";
import { StoreContext } from "../../../store";
import { SKButton, SKSubmitButton } from "../../ui";

export const generateValidationSchema = (t, newRoomAvailable, isEdit = false) =>
  Yup.object().shape({
    propertyId: Yup.string().required(t("Required")),
    name: Yup.string().required(t("Required")),
    room_status: Yup.string().required(t("Required")),
    ...(isEdit
      ? {}
      : {
          totalRooms: Yup.number()
            .typeError(t("Must be a number"))
            .min(1, t("Must be at least 1"))
            .test("max-rooms", t("Cannot exceed maximum rooms"), (value) => {
              if (newRoomAvailable && newRoomAvailable > 0 && newRoomAvailable !== Infinity) {
                return Number(value) <= newRoomAvailable;
              }
              return true;
            })
            .required(t("Required")),
        }),
    price: Yup.object().shape({
      daily: Yup.number().nullable(),
      weekly: Yup.number().nullable(),
      monthly: Yup.number().nullable(),
      yearly: Yup.number().nullable(),
    }),
    description: Yup.string().nullable(),
    room_amenities: Yup.string().nullable(),
  });

const RoomForm = observer(function RoomForm({
  mode = "create",
  room,
  propertyId,
  initialValues: customInitialValues,
  onSubmit,
  onSuccess,
  onCancel,
  isDialog = false,
  submitLabel,
  loadingLabel,
  hideActions = false,
  actions,
  innerRef,
}) {
  const { t } = useTranslation("common");
  const store = useContext(StoreContext);

  const isEdit = mode === "edit" || Boolean(room?._id || customInitialValues?._id);

  const [isLoading, setIsLoading] = useState(false);

  const initialPropertyId =
    propertyId || room?.propertyId || customInitialValues?.propertyId || store.property?.selected?._id || "";
  const [selectedProperties, setSelectedProperties] = useState(initialPropertyId);

  const limitRoom =
    store.subscription?.selected?.usage?.activeRoom ||
    store.subscription?.selected?.features?.maxRoom ||
    store.subscription?.selected?.maxRoom;
  const newRoomAvailable = (limitRoom ?? Infinity) - (store.room?.items?.length || 0);

  // Ensure properties are loaded
  useEffect(() => {
    if (!store.property?.items || store.property.items.length === 0) {
      store.property?.fetch();
    }
  }, [store.property]);

  // Sync selectedProperties when props or store change
  useEffect(() => {
    const propId =
      propertyId ||
      room?.propertyId ||
      customInitialValues?.propertyId ||
      store.property?.selected?._id ||
      (store.property?.items?.length === 1 ? store.property.items[0]._id : "");
    if (propId) {
      setSelectedProperties(propId);
    }
  }, [
    propertyId,
    room?.propertyId,
    customInitialValues?.propertyId,
    store.property?.selected?._id,
    store.property?.items,
  ]);

  const properties = useMemo(() => {
    return (store.property?.items || []).map(({ _id, name }) => ({
      id: _id,
      value: _id,
      label: name,
    }));
  }, [store.property?.items]);

  const handlePropertiesChange = (selectedValue, setFieldValue) => {
    const val = selectedValue.target.value;
    setSelectedProperties(val);
    if (setFieldValue) {
      setFieldValue("propertyId", val);
    }
  };

  const handleDefaultSubmit = useCallback(
    async (values, { setSubmitting }) => {
      setIsLoading(true);
      const { ...updatedRoomPart } = values;
      const { totalRooms, name } = updatedRoomPart;
      const price = {
        daily: Number(updatedRoomPart.price?.daily) || 0,
        weekly: Number(updatedRoomPart.price?.weekly) || 0,
        monthly: Number(updatedRoomPart.price?.monthly) || 0,
        yearly: Number(updatedRoomPart.price?.yearly) || 0,
      };

      try {
        if (isEdit) {
          const roomPayload = {
            ...updatedRoomPart,
            _id: values._id || room?._id || store.room?.selected?._id,
            price,
          };

          const { status, data, error_code } = await store.room.update(roomPayload);
          if (status !== 200) {
            switch (status) {
              case 422:
                if (error_code === "ROOM_HAS_ACTIVE_TENANT") {
                  store.pushToastMessage({
                    message: t("Room cannot be updated as it has an active tenant"),
                    severity: "error",
                  });
                } else {
                  store.pushToastMessage({
                    message: t("Room name is missing"),
                    severity: "error",
                  });
                }
                break;
              case 403:
                store.pushToastMessage({
                  message: t("You are not allowed to update the room"),
                  severity: "error",
                });
                break;
              default:
                store.pushToastMessage({
                  message: t("Something went wrong"),
                  severity: "error",
                });
                break;
            }
            return;
          }

          store.pushToastMessage({
            message: t("Success update room"),
            severity: "success",
          });

          store.room?.setSelected(data);
          if (store.room?.fetchPaginate) {
            await store.room.fetchPaginate(store.room.filters);
          }

          if (onSuccess) {
            await onSuccess(data);
          }
        } else {
          const bulkCreation = Number(totalRooms) > 1;

          if (bulkCreation) {
            const rooms = { rooms: [] };
            for (let i = 1; i <= Number(totalRooms); i++) {
              const r = {
                ...updatedRoomPart,
                name: `${name} ${i}`,
                price,
              };
              rooms.rooms.push(r);
            }

            const { status, data, error_code } = await store.room.createMany(rooms);
            if (status !== 200) {
              if (error_code) {
                switch (error_code) {
                  case "MAX_ROOMS_REACHED":
                    store.pushToastMessage({
                      message: t("You have reached the maximum number of rooms for your current plan."),
                      severity: "error",
                    });
                    return;
                  case "CAN_NOT_ADD_ROOMS":
                    store.pushToastMessage({
                      message: t("Cannot add room. Please contact the Superkos team via WhatsApp."),
                      severity: "error",
                    });
                    return;
                  default:
                    break;
                }
              }
              switch (status) {
                case 422:
                  store.pushToastMessage({
                    message: t("Room name is missing"),
                    severity: "error",
                  });
                  break;
                case 403:
                  store.pushToastMessage({
                    message: t("You are not allowed to add a room"),
                    severity: "error",
                  });
                  break;
                case 409:
                  store.pushToastMessage({
                    message: t("The room already exists"),
                    severity: "error",
                  });
                  break;
                default:
                  store.pushToastMessage({
                    message: t("Something went wrong"),
                    severity: "error",
                  });
                  break;
              }
              return;
            }

            store.pushToastMessage({
              message: t("Success create new rooms"),
              severity: "success",
            });

            if (store.room?.fetchPaginate) {
              await store.room.fetchPaginate(store.room.filters);
            }

            if (onSuccess) {
              await onSuccess(data);
            }
          } else {
            const roomPayload = {
              ...updatedRoomPart,
              price,
            };

            const { status, data, error_code } = await store.room.create(roomPayload);
            if (status !== 200) {
              if (error_code) {
                switch (error_code) {
                  case "MAX_ROOMS_REACHED":
                    store.pushToastMessage({
                      message: t("You have reached the maximum number of rooms for your current plan."),
                      severity: "error",
                    });
                    return;
                  case "CAN_NOT_ADD_ROOMS":
                    store.pushToastMessage({
                      message: t("Cannot add room. Please contact the Superkos team via WhatsApp."),
                      severity: "error",
                    });
                    return;
                  default:
                    break;
                }
              }
              switch (status) {
                case 422:
                  store.pushToastMessage({
                    message: t("Room name is missing"),
                    severity: "error",
                  });
                  break;
                case 403:
                  store.pushToastMessage({
                    message: t("You are not allowed to add a room"),
                    severity: "error",
                  });
                  break;
                case 409:
                  store.pushToastMessage({
                    message: t("The room already exists"),
                    severity: "error",
                  });
                  break;
                default:
                  store.pushToastMessage({
                    message: t("Something went wrong"),
                    severity: "error",
                  });
                  break;
              }
              return;
            }

            store.pushToastMessage({
              message: t("Success create new room"),
              severity: "success",
            });

            store.room?.setSelected(data);
            if (store.room?.fetchPaginate) {
              await store.room.fetchPaginate(store.room.filters);
            }

            if (onSuccess) {
              await onSuccess(data);
            }
          }
        }
      } catch (error) {
        console.error(error);
        store.pushToastMessage({
          message: t("Something went wrong"),
          severity: "error",
        });
      } finally {
        setIsLoading(false);
        setSubmitting(false);
      }
    },
    [isEdit, room?._id, store, t, onSuccess],
  );

  const initialValues = useMemo(() => {
    const source = room || (isEdit ? store.room?.selected : null) || {};
    const propId = source.propertyId || propertyId || selectedProperties || store.property?.selected?._id || "";

    return {
      _id: source._id || undefined,
      name: source.name,
      propertyId: propId,
      totalRooms: 1,
      price: {
        daily: source.price?.daily ?? 0,
        weekly: source.price?.weekly ?? 0,
        monthly: source.price?.monthly ?? 0,
        yearly: source.price?.yearly ?? 0,
      },
      room_status: source.room_status, // (isEdit ? "clean" : ""),
      room_amenities: source.room_amenities || null,
      description: source.description || null,
      displaySettings: {
        pricePeriod: source.displaySettings?.pricePeriod || null,
      },
      ...customInitialValues,
    };
  }, [
    room,
    isEdit,
    store.room?.selected,
    propertyId,
    selectedProperties,
    store.property?.selected?._id,
    customInitialValues,
  ]);

  const ContentWrapper = isDialog ? DialogContent : Box;
  const ActionsWrapper = isDialog ? DialogActions : Box;

  return (
    <Formik
      innerRef={innerRef}
      initialValues={initialValues}
      validationSchema={generateValidationSchema(t, newRoomAvailable, isEdit)}
      onSubmit={onSubmit || handleDefaultSubmit}
      enableReinitialize
    >
      {({ isSubmitting, isValid, dirty, setFieldValue }) => {
        const formFields = (
          <>
            {!isEdit && (
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <SelectField
                    label={t("Property")}
                    name="propertyId"
                    values={properties}
                    value={selectedProperties}
                    disabled={Boolean(propertyId) || isEdit}
                    onChange={(e) => {
                      handlePropertiesChange(e, setFieldValue);
                    }}
                  />
                </Grid>
              </Grid>
            )}

            {!selectedProperties && !propertyId && !isEdit && (
              <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
                {t("Select a property first")}
              </Typography>
            )}

            <Collapse in={isEdit || Boolean(selectedProperties || propertyId)}>
              <Box pt={1}>
                <Grid container spacing={2}>
                  <Grid item xs={isEdit ? 12 : 8}>
                    <TextField label={t("Room Name") + " *"} name="name" />
                  </Grid>

                  {!isEdit && (
                    <>
                      <Grid item xs={4}>
                        <NumberField
                          label={t("Number of Rooms") + " *"}
                          name="totalRooms"
                          min={1}
                          max={newRoomAvailable > 0 && newRoomAvailable !== Infinity ? newRoomAvailable : undefined}
                        />
                      </Grid>

                      <Grid item xs={12} style={{ paddingTop: 0, paddingBottom: 0, marginTop: -12 }}>
                        <FormHelperText>
                          {t("More than 1 room will create consecutive names automatically (e.g., Room 1, Room 2).")}
                        </FormHelperText>
                      </Grid>
                    </>
                  )}

                  <Grid item xs={12}>
                    <SelectField
                      label={t("Room Status") + " *"}
                      name="room_status"
                      values={[
                        { id: "clean", value: "clean", label: t("Clean") },
                        { id: "dirty", value: "dirty", label: t("Dirty") },
                        { id: "out_of_order", value: "out_of_order", label: t("Out of Order") },
                      ]}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <CustomPriceField label={t("Daily")} name="price.daily" />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <CustomPriceField label={t("Weekly")} name="price.weekly" />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <CustomPriceField label={t("Monthly")} name="price.monthly" />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <CustomPriceField label={t("Yearly")} name="price.yearly" />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField label={t("Description")} name="description" multiline maxRows={5} />
                  </Grid>

                  {isEdit && (
                    <>
                      <Grid item xs={12}>
                        <TextField label={t("Room Amenities")} name="room_amenities" multiline maxRows={5} />
                      </Grid>
                      <Grid item xs={12}>
                        <SelectField
                          label={t("Display Price")}
                          name="displaySettings.pricePeriod"
                          values={[
                            { id: "daily", value: "daily", label: t("Daily") },
                            { id: "weekly", value: "weekly", label: t("Weekly") },
                            { id: "monthly", value: "monthly", label: t("Monthly") },
                            { id: "yearly", value: "yearly", label: t("Yearly") },
                            // { id: "hidden", value: "hidden", label: t("Hidden") },
                          ]}
                        />
                      </Grid>
                    </>
                  )}
                </Grid>
              </Box>
            </Collapse>
          </>
        );

        return (
          <Form autoComplete="off">
            <ContentWrapper style={isDialog ? { paddingTop: 0, paddingBottom: 8 } : undefined}>
              {formFields}
            </ContentWrapper>

            {!hideActions && (
              <ActionsWrapper
                style={
                  isDialog
                    ? { padding: "12px 24px" }
                    : { display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 8, marginTop: 24 }
                }
              >
                {actions ? (
                  actions({ isSubmitting, isLoading, isValid, dirty })
                ) : (
                  <>
                    {onCancel && (
                      <SKButton onClick={onCancel} size="small" disabled={isLoading || isSubmitting}>
                        {t("Cancel")}
                      </SKButton>
                    )}
                    <SKSubmitButton
                      size="small"
                      isLoading={isLoading || isSubmitting}
                      disabled={!isEdit && !selectedProperties && !propertyId}
                      label={submitLabel || t("Save")}
                      loadingLabel={loadingLabel || t("Saving...")}
                      spinnerPlacement="start"
                    />
                  </>
                )}
              </ActionsWrapper>
            )}
          </Form>
        );
      }}
    </Formik>
  );
});

export default RoomForm;
